import { Alert, Button, PageHeader, Result, Space, Spin, Table, Upload, message } from 'antd'
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons'
import axios from 'axios'
import dayjs from 'dayjs'
import { getAuth, getIdToken } from 'firebase/auth'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQueries, useQueryClient } from 'react-query'
import { useGetLookup } from '../../hooks/api/useLookups'
import { recordBulkTalentIdUpload } from '../../hooks/bulkTalentIdUploadStorage'
import { makeColumns, renderExpandedRow } from './columns'
import { buildTemplate, parseCsv, rowFromCsv, toCsvActions, toCsvMovements } from './csv'

const CURRENT_YEAR = dayjs().year()

const getToken = async (queryClient) => {
  return queryClient.getQueryData('token')?.token || getIdToken(getAuth().currentUser, true)
}

const getSelectedUids = (search) => {
  const params = new URLSearchParams(search)
  return (params.get('selected') || '')
    .split(',')
    .map(uid => uid.trim())
    .filter(Boolean)
}

const sanitizeMovementsForSubmit = (movements = []) => {
  return movements
    .map((movement) => ({
      movementType: movement?.movementType || '',
      time: movement?.time || '',
      nextMove: movement?.nextMove || ''
    }))
    .filter(movement => movement.movementType || movement.time || movement.nextMove)
}

const nullableString = (value) => {
  if (typeof value !== 'string') return value ?? null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

const toRowFromTalentId = (employee, talentID) => ({
  employeeUid: employee.uid,
  firstName: employee.firstName || employee.person?.firstName || '',
  lastName: employee.lastName || employee.person?.lastName || '',
  talentOperationalReview: {
    assessmentOfPotential: talentID?.talentOperationalReview?.assessmentOfPotential || '',
    skillDevelopmentNeeds: talentID?.talentOperationalReview?.skillDevelopmentNeeds || [],
    nominatedFor: talentID?.talentOperationalReview?.nominatedFor || '',
    talentPoolLevel: talentID?.talentOperationalReview?.talentPoolLevel || '',
    isFlightRisk: talentID?.talentOperationalReview?.isFlightRisk || false,
    talentReviewComments: talentID?.talentOperationalReview?.talentReviewComments || '',
    managersComments: talentID?.talentOperationalReview?.managersComments || '',
    managerOnceRemovedComments: talentID?.talentOperationalReview?.managerOnceRemovedComments || '',
    careerActionsDoc: (talentID?.talentOperationalReview?.careerActionsDoc || []).map(item => ({ ...item, date: item?.date ? dayjs(item.date) : null }))
  },
  talentDisciplineReview: {
    assessmentOfPotential: talentID?.talentDisciplineReview?.assessmentOfPotential || '',
    ultimatePotential: talentID?.talentDisciplineReview?.ultimatePotential || '',
    mpaPotential: talentID?.talentDisciplineReview?.mpaPotential || '',
    promotability: talentID?.talentDisciplineReview?.promotability || '',
    nextMove: talentID?.talentDisciplineReview?.nextMove || '',
    action: talentID?.talentDisciplineReview?.action || '',
    reviewComments: talentID?.talentDisciplineReview?.reviewComments || ''
  },
  talentOperationalMovements: (talentID?.talentOperationalMovements || []).map((movement) => ({
    movementType: movement?.movementType || '',
    time: movement?.time || '',
    nextMove: movement?.nextMove || ''
  })),
  talentOperationalMovementsToDelete: [],
  status: 'pending',
  errorMessage: '',
  dirtyAfterSuccess: false
})

export const BulkTalentID = () => {
  const history = useHistory()
  const { search } = useLocation()
  const queryClient = useQueryClient()
  const selectedUids = useMemo(() => getSelectedUids(search), [search])
  const [rows, setRows] = useState([])
  const [result, setResult] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [initializedFromApi, setInitializedFromApi] = useState(false)

  const selectedEmployeesQueries = useQueries(
    selectedUids.map(uid => ({
      queryKey: ['employee', uid],
      queryFn: async () => axios.get(
        `${process.env.REACT_APP_API_URL}/talent/employees/${uid}`,
        {
          headers: {
            Authorization: `Bearer ${await getToken(queryClient)}`,
            'X-Tenant-Id': process.env.REACT_APP_TENANT_ID
          }
        }
      ).then(res => res.data.data),
      enabled: !!uid,
      retry: false
    }))
  )

  const lastYearTalentQueries = useQueries(
    selectedUids.map(uid => ({
      queryKey: ['talentID', uid, CURRENT_YEAR - 1],
      queryFn: async () => axios.get(
        `${process.env.REACT_APP_API_URL}/talent/employees/${uid}/talentId/${CURRENT_YEAR - 1}`,
        {
          headers: {
            Authorization: `Bearer ${await getToken(queryClient)}`,
            'X-Tenant-Id': process.env.REACT_APP_TENANT_ID
          }
        }
      ).then(res => res.data),
      enabled: false,
      retry: false
    }))
  )

  const lookups = {
    aopLookups: useGetLookup({ group: 'TalentOverview', key: 'AssessmentOfPotential' }).data || [],
    skillLookups: useGetLookup({ group: 'TalentOverview', key: 'SkillDevelopmentNeeds' }).data || [],
    opLookups: useGetLookup({ group: 'TalentOverview', key: 'OperationalReviewNomination' }).data || [],
    poolLookups: useGetLookup({ group: 'TalentOverview', key: 'TalentPoolLevel' }).data || [],
    promLookups: useGetLookup({ group: 'TalentOverview', key: 'Promotability' }).data || [],
    nextLookups: useGetLookup({ group: 'Dynamic', key: 'JobTitle' }).data || [],
    lowLookups: useGetLookup({ group: 'Dynamic', key: 'LevelOfWork' }).data || []
  }

  const employeeLoading = selectedEmployeesQueries.some(query => query.isLoading)
  const employeeError = selectedEmployeesQueries.find(query => query.isError)

  useEffect(() => {
    if (initializedFromApi || employeeLoading || employeeError) return
    if (!selectedEmployeesQueries.length) return
    const allDone = selectedEmployeesQueries.every(query => query.data)
    if (!allDone) return

    setRows(selectedEmployeesQueries.map(query => toRowFromTalentId(query.data, null)))
    setInitializedFromApi(true)
  }, [initializedFromApi, employeeLoading, employeeError, selectedEmployeesQueries])

  const dispatchRowUpdate = (employeeUid, actionType, field, value) => {
    setRows(current => current.map(row => {
      if (row.employeeUid !== employeeUid) return row
      const nextRow = {
        ...row,
        dirtyAfterSuccess: row.status === 'success' ? true : row.dirtyAfterSuccess,
        errorMessage: row.status === 'failed' ? '' : row.errorMessage
      }
      if (actionType === 'field-operation') {
        nextRow.talentOperationalReview = {
          ...row.talentOperationalReview,
          [field]: value
        }
      } else if (actionType === 'field-discipline') {
        nextRow.talentDisciplineReview = {
          ...row.talentDisciplineReview,
          [field]: value
        }
      } else if (actionType === 'field') {
        nextRow[field] = value
      }
      return nextRow
    }))
  }

  const columns = makeColumns({ lookups, dispatchRowUpdate })

  const selectedLabel = (() => {
    if (rows.length === 1) return `Selected: ${rows[0].firstName || '1 person'}`
    if (rows.length === 2) return `Selected: ${(rows[0].firstName || '1')} & ${(rows[1].firstName || '1')}`
    if (rows.length >= 3 && rows.length <= 4) return `Selected: ${(rows[0].firstName || '1')}, ${(rows[1].firstName || '1')} & ${rows.length - 2} others`
    if (rows.length >= 5) return `Selected: ${rows.length} people`
    return ''
  })()

  const downloadCsvTemplate = () => {
    const text = buildTemplate(rows.map(row => ({ uid: row.employeeUid, firstName: row.firstName, lastName: row.lastName })))
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `bulk-talent-id-template-${CURRENT_YEAR}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const applyCsv = (file) => {
    const reader = new FileReader()
    reader.onload = () => {
      const parsed = parseCsv(reader.result)
      const byUid = new Map(rows.map(row => [row.employeeUid, row]))
      const nextRows = []
      parsed.forEach(csvRow => {
        const employee = byUid.get(csvRow.employeeUid)
        if (!employee) return
        const built = rowFromCsv(csvRow, { uid: employee.employeeUid, firstName: employee.firstName, lastName: employee.lastName })
        nextRows.push({ ...employee, ...built, status: employee.status, dirtyAfterSuccess: employee.status === 'success' ? true : employee.dirtyAfterSuccess })
      })
      if (!nextRows.length) {
        message.warning('No matching selected employees were found in CSV.')
        return
      }
      setRows(current => current.map(row => nextRows.find(next => next.employeeUid === row.employeeUid) || row))
      setResult(null)
      message.success(`Imported ${nextRows.length} rows from CSV.`)
    }
    reader.readAsText(file)
    return false
  }

  async function fetchUserList (name) {
    const token = await getToken(queryClient)
    return axios.get(
      `${process.env.REACT_APP_API_URL}/talent/employees?q=${name || ''}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Id': process.env.REACT_APP_TENANT_ID
        }
      }
    ).then(res => {
      return (res?.data?.data || []).map(person => ({
        label: person.person.firstName + ' ' + person.person.lastName,
        value: person.person.firstName + ' ' + person.person.lastName
      }))
    })
  }

  const populateFromLastYear = async () => {
    setResult(null)
    const refetchResults = await Promise.all(lastYearTalentQueries.map(query => query.refetch()))
    const lastYearByUid = new Map(
      selectedUids.map((uid, index) => [uid, refetchResults[index]?.data || null])
    )

    setRows(current => current.map((row) => {
      const lastYearData = lastYearByUid.get(row.employeeUid)
      if (!lastYearData) return row
      const hydrated = toRowFromTalentId({ uid: row.employeeUid, firstName: row.firstName, lastName: row.lastName }, lastYearData)
      return {
        ...row,
        ...hydrated,
        status: row.status,
        dirtyAfterSuccess: row.status === 'success' ? true : row.dirtyAfterSuccess
      }
    }))
    message.success('Rows populated from last year records.')
  }

  const submitRow = async (row) => {
    const payload = {
      talentOperationalReview: {
        ...row.talentOperationalReview,
        assessmentOfPotential: nullableString(row.talentOperationalReview?.assessmentOfPotential),
        nominatedFor: nullableString(row.talentOperationalReview?.nominatedFor),
        talentPoolLevel: nullableString(row.talentOperationalReview?.talentPoolLevel),
        careerActionsDoc: (row.talentOperationalReview?.careerActionsDoc || []).map(action => ({
          ...action,
          date: action?.date ? (dayjs.isDayjs(action.date) ? action.date.toISOString() : dayjs(action.date).toISOString()) : null
        }))
      },
      talentDisciplineReview: {
        ...row.talentDisciplineReview,
        assessmentOfPotential: nullableString(row.talentDisciplineReview?.assessmentOfPotential),
        ultimatePotential: nullableString(row.talentDisciplineReview?.ultimatePotential),
        mpaPotential: nullableString(row.talentDisciplineReview?.mpaPotential),
        promotability: nullableString(row.talentDisciplineReview?.promotability),
        nextMove: nullableString(row.talentDisciplineReview?.nextMove)
      },
      talentOperationalMovements: sanitizeMovementsForSubmit(row.talentOperationalMovements),
      talentOperationalMovementsToDelete: row.talentOperationalMovementsToDelete || []
    }

    const token = await getToken(queryClient)
    return axios.put(
      `${process.env.REACT_APP_API_URL}/talent/employees/${row.employeeUid}/talentId/${CURRENT_YEAR}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Id': process.env.REACT_APP_TENANT_ID
        }
      }
    )
  }

  const submitAll = async () => {
    setIsSubmitting(true)
    setResult(null)

    let submittedCount = 0
    for (const row of rows) {
      if (row.status === 'success' && !row.dirtyAfterSuccess) {
        continue
      }

      setRows(current => current.map(item => item.employeeUid === row.employeeUid ? { ...item, status: 'submitting', errorMessage: '' } : item))
      try {
        await submitRow(row)
        submittedCount += 1
        recordBulkTalentIdUpload(row.employeeUid)
        setRows(current => current.map(item => item.employeeUid === row.employeeUid ? { ...item, status: 'success', dirtyAfterSuccess: false, errorMessage: '' } : item))
      } catch (error) {
        const reason = error?.response?.data?.message || error.message || 'Unknown error'
        setRows(current => current.map(item => item.employeeUid === row.employeeUid ? { ...item, status: 'failed', errorMessage: reason } : item))
        setResult({ status: 'warning', title: 'Bulk upload stopped on first failure', subTitle: `${row.firstName} ${row.lastName}: ${reason}` })
        setIsSubmitting(false)
        return
      }
    }

    setResult({ status: 'success', title: 'Bulk upload complete', subTitle: `Submitted ${submittedCount} row${submittedCount === 1 ? '' : 's'} for ${CURRENT_YEAR}.` })
    setIsSubmitting(false)
  }

  if (!selectedUids.length) {
    return (
      <Result
        status='warning'
        title='No selected employees'
        subTitle='Select employees first from the Employees page.'
        extra={<Button onClick={() => history.push('/employees')}>Back to employees</Button>}
      />
    )
  }

  if (employeeLoading) {
    return (
      <div className='w-full flex justify-center py-24'>
        <Spin size='large' />
      </div>
    )
  }

  if (employeeError) {
    return <Result status='warning' title='Unable to fetch selected employees.' />
  }

  return (
    <div className='employees__page'>
      <PageHeader style={{ paddingLeft: 0 }} title='Bulk Talent ID Upload' />
      {!!selectedLabel && <Alert className='mb-4' type='info' message={selectedLabel} />}
      {result && (
        <Result
          className='mb-6'
          status={result.status}
          title={result.title}
          subTitle={result.subTitle}
        />
      )}
      <Space className='mb-4' wrap>
        <Button icon={<DownloadOutlined />} onClick={downloadCsvTemplate}>Download CSV template</Button>
        <Upload beforeUpload={applyCsv} accept='.csv' showUploadList={false}>
          <Button icon={<UploadOutlined />}>Import CSV</Button>
        </Upload>
        <Button onClick={populateFromLastYear}>Populate from last year</Button>
        <Button type='primary' className='bg-blue-400' loading={isSubmitting} onClick={submitAll}>
          Submit current year ({CURRENT_YEAR})
        </Button>
      </Space>
      <Table
        rowKey='employeeUid'
        dataSource={rows}
        columns={columns}
        scroll={{ x: 'max-content' }}
        tableLayout='fixed'
        size='middle'
        expandable={{ expandedRowRender: row => renderExpandedRow({ row, dispatchRowUpdate, lookups, fetchUserList }) }}
        rowClassName={(row) => row.status === 'failed' ? 'bg-red-50' : ''}
        pagination={{ pageSize: 10 }}
      />
      <Alert
        className='mt-4'
        type='info'
        message='CSV format'
        description={`Use "|" to separate fields inside a movement/action and ";" to separate multiple movement/action entries. Example movement: promotion|0-12 months|Senior Engineer`}
      />
      <Alert
        className='mt-3'
        type='info'
        message={`Career action CSV format: action|YYYY-MM-DD|responsibility;action2|YYYY-MM-DD|responsibility2`}
      />
      <Alert
        className='mt-3'
        type='info'
        message={`Export columns include talentOperationalMovements (${toCsvMovements(rows[0]?.talentOperationalMovements || []) || '...'}), careerActionsDoc (${toCsvActions(rows[0]?.talentOperationalReview?.careerActionsDoc || []) || '...'})`}
      />
    </div>
  )
}
